import type React from 'react';
import { IconAlertTriangle, IconCircleCheckFilled, IconClock, IconFileInvoice, IconX } from '@tabler/icons-react';
import { ServiceRequestStatus, ServiceRequestUrgency } from '../interfaces/heavyDutyMachinery/IServiceRequest';
import { PillTone } from '../theme/creamyTokens';
import { TRANSLATIONS } from './translations';

/**
 * How each status of a quote request looks and reads.
 *
 * Exhaustive `Record`s on purpose, same as the booking ones: TypeScript then
 * forces every one of them to be touched the day an eighth status appears,
 * instead of a screen quietly rendering an empty badge.
 */
export const SERVICE_REQUEST_STATUS_ICON: Record<ServiceRequestStatus, React.ElementType> = {
  Sent: IconClock,
  Quoted: IconFileInvoice,
  Accepted: IconCircleCheckFilled,
  Declined: IconX,
  Expired: IconAlertTriangle,
  Cancelled: IconX,
  Converted: IconCircleCheckFilled,
};

/**
 * `Quoted` is warning rather than success deliberately: there is a live
 * decision on the table and somebody has to make it. A green badge would read
 * as "done" on the one screen where nothing is done yet.
 */
export const SERVICE_REQUEST_STATUS_TONES: Record<ServiceRequestStatus, PillTone> = {
  Sent: 'default',
  Quoted: 'warning',
  Accepted: 'success',
  Declined: 'error',
  Expired: 'error',
  Cancelled: 'default',
  Converted: 'success',
};

export const SERVICE_REQUEST_STATUS_LABEL_KEYS: Record<ServiceRequestStatus, string> = {
  Sent: TRANSLATIONS.SERVICE_REQUEST_STATUS_SENT,
  Quoted: TRANSLATIONS.SERVICE_REQUEST_STATUS_QUOTED,
  Accepted: TRANSLATIONS.SERVICE_REQUEST_STATUS_ACCEPTED,
  Declined: TRANSLATIONS.SERVICE_REQUEST_STATUS_DECLINED,
  Expired: TRANSLATIONS.SERVICE_REQUEST_STATUS_EXPIRED,
  Cancelled: TRANSLATIONS.SERVICE_REQUEST_STATUS_CANCELLED,
  Converted: TRANSLATIONS.SERVICE_REQUEST_STATUS_CONVERTED,
};

export const SERVICE_REQUEST_URGENCY_LABEL_KEYS: Record<ServiceRequestUrgency, string> = {
  Flexible: TRANSLATIONS.SERVICE_REQUEST_URGENCY_FLEXIBLE,
  ThisWeek: TRANSLATIONS.SERVICE_REQUEST_URGENCY_THIS_WEEK,
  Urgent: TRANSLATIONS.SERVICE_REQUEST_URGENCY_URGENT,
};

export const SERVICE_REQUEST_URGENCY_TONES: Record<ServiceRequestUrgency, PillTone> = {
  Flexible: 'default',
  ThisWeek: 'warning',
  Urgent: 'error',
};

/** Still being negotiated -- somebody can still act on it. */
export const OPEN_SERVICE_REQUEST_STATUSES: ServiceRequestStatus[] = ['Sent', 'Quoted', 'Accepted'];
